/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';
import { when } from 'mobx';

import { IDlvryAlternativeProductsData } from './dlvry-alternative-products.data';
import { IDlvryAlternativeProductsProps } from './dlvry-alternative-products.props.autogenerated';
import { Heading, IModuleProps, INodeProps, NodeTag, SingleSlideCarousel } from '@msdyn365-commerce-modules/utilities';
import { AsyncResult, format, ProductList, ProductSearchResult, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import ProductComponent from '../../themes/dlvry-adventureworks/views/components/product.component';
import { getCustomerAgreements } from '../../shared-utils/agreement-product-utils';
import { onBuyHasAccessValidator } from '../../shared-utils/attributes/on-buy-access-validator';
import PQueue from 'p-queue';
import { ArrayExtensions, getOrderTemplates, GetOrderTemplatesInput, OrderTemplate, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getActivePricesAsync, getByIdsAsync, getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { getProductListsByProductIdsAsync } from '../../actions/get-product-lists-by-product-ids';
import { ProductSearchResultExt } from '../../shared-utils/types';
import { createInventoryAvailabilitySearchCriteria, DeliveryMode, IProductInventoryInformation, mapAggregatedProductInventoryInformation } from '../../components/common/product-inventory-utils';
// @ts-expect-error
import { getFullProductsAsync } from '../../actions/get-full-products';
import { getDiscountLines } from '../../themes/dlvry-adventureworks/views/components/price.component';

export interface IDlvryAlternativeProductsViewProps extends IDlvryAlternativeProductsProps<IDlvryAlternativeProductsData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    products: IProductComponentViewProps[];
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}

interface IDlvryAlternativeProductsState {
    hasSortedProducts?: boolean;
    orderTemplates: OrderTemplate[] | undefined;
    retrievingActivePrices: boolean;
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
    availabilities?: IProductInventoryInformation[];
}

export function getInventoryLabel(productRecordId: number, productInventoryStatus: IProductInventoryInformation[] | undefined): string | undefined {
    if(productInventoryStatus){
        const shippingInventory = productInventoryStatus.find(shipping => shipping.ProductAvailableQuantity.ProductId === productRecordId);
        if(shippingInventory && shippingInventory.StockLevelLabel){
            return shippingInventory.StockLevelLabel;
        }
    }

    return undefined;
}

/**
 *
 * DlvryAlternativeProducts component
 * @extends {React.PureComponent<IDlvryAlternativeProductsProps<IDlvryAlternativeProductsData>>}
 */
class DlvryAlternativeProducts extends React.PureComponent<IDlvryAlternativeProductsProps<IDlvryAlternativeProductsData>, IDlvryAlternativeProductsState> {

    private readonly _cartQueue: PQueue;

    get useExperimentalAlternativeProducts(): boolean {
        return this.props.config.useExperimentalAlternativeProducts === true;
    }

    get products(): AsyncResult<ProductSearchResultExt[]> {
        return this.useExperimentalAlternativeProducts ? this.props.data.productsExperimental : this.props.data.products;
    }

    constructor(props: IDlvryAlternativeProductsProps<IDlvryAlternativeProductsData>) {
        super(props);
        this.state = {
            orderTemplates: undefined,
            retrievingActivePrices: true,
            simpleProducts: [],
            unitConversions: []
        };
        this._cartQueue = new PQueue({ concurrency: 1 });
    }

    public componentDidMount(): void {
        void when(
            () => this.products.status === 'SUCCESS',
            () => {
                void this._getActivePrices();
                void this._getSimpleProducts();
                void this._getUnitConversions();
                void this._getProductListsByProductIds();
                void this._getEstimatedAvailabilities();

                /* void getFullProductsAsync(
                    { callerContext: this.props.context.actionContext },
                    this.props.data.products.result?.map(x => x.RecordId) || [],
                    this.props.context.request.apiSettings.catalogId
                ).then((result) => {
                    console.log('Full products retrieved:', JSON.parse(JSON.stringify(result)));
                }); */
            }
        );
    }

    public render(): JSX.Element | null {
        const { resources } = this.props;
        const { className, heading } = this.props.config;

        if (!this.products.result || this.products.result.length === 0) {
            return null;
        }

        const viewProps: IDlvryAlternativeProductsViewProps = {
            ...this.props,
            heading: heading && (
                <Heading className='ms-product-collection__heading' headingTag={heading.tag} text={heading.text} />
            ),
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id
            },
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection', className)
            },
            products: this.products.result?.map((product, index) =>
                this._getProduct(product, index)
            ) || []
        };

        return this.props.renderView(viewProps);
    }

    private _getProduct(product: ProductSearchResult, index: number): IProductComponentViewProps {
        const {
            context,
            config: { imageSettings },
            data: { orgInformation },
            resources
        } = this.props;
        const { hasSortedProducts } = this.state;

        // Show a placeholder card until we can sort products by price discounts since we don't retrieve pricing information while SSR
        const availablity = hasSortedProducts && this.state.availabilities?.find(x => x.ProductAvailableQuantity.ProductId === product.RecordId) || undefined;
        const simpleProduct = hasSortedProducts && this.state.simpleProducts.find(x => x.RecordId === product.RecordId) || undefined;
        const unitConversions = hasSortedProducts && this.state.unitConversions.filter(x => x.ProductRecordId === product.RecordId) || undefined;
        const inventoryLabel = hasSortedProducts && getInventoryLabel(product.RecordId, this.state.availabilities) || undefined;

        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: product.RecordId
            },
            productComponent: (
                <ProductComponent
                    context={context}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={format(resources.ratingAriaLabel, product.AverageRating || 0, 5)}
                    priceNotLoggedInText={resources.priceNotLoggedInText}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{
                        cart: this.props.data.cart.result,
                        product: product,
                        customerInformation: this.props.data.orgInformation.result,
                        customerAgreements: getCustomerAgreements(context, this.props.data.orgInformation.result),
                        simpleProduct: simpleProduct,
                        unitConversions: unitConversions
                    }}
                    inventoryLabel={inventoryLabel}
                    inventoryStatus={availablity}
                    cartQueue={this._cartQueue}
                    disableBuyButton={!this.state.hasSortedProducts || this.state.retrievingActivePrices}
                    showPrice={this.state.hasSortedProducts && !this.state.retrievingActivePrices}
                    showAsPlaceholder={!this.state.hasSortedProducts}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                    onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                    addToCartText={resources.addToCartButtonText}
                    updateCartText={resources.updateCartText}
                    removeFromCartText={resources.removeFromCartText}
                    outOfStockText={resources.outOfStockText}
                    priceErrorText={resources.priceErrorText}
                    toastStrings={{
                        errorHeader: resources.toastErrorHeader,
                        productsAddedToCart: resources.productsAddedToCart,
                        productsRemovedFromCart: resources.productsRemovedFromCart,
                        productsUpdatedInCart: resources.productsUpdatedInCart,
                        goToCart: resources.toastGoToCart,
                        noAccessToProduct: resources.toastNoAccessToProduct
                    }}
                    addToOrderTemplateResources={{
                        addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
                        noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
                        noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
                        createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
                        createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
                        cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
                        selectTemplatesText: resources.selectTemplatesText,
                        addToTemplateButtonText: resources.addToTemplateButtonText,
                        lineItemsText: resources.lineItemsText,
                        createOrderTemplateHeader: resources.createOrderTemplateHeader,
                        orderTemplateTitle: resources.orderTemplateTitle,
                        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
                        createOrderTemplateDescription: resources.createOrderTemplateDescription,
                        defaultOrderTemplateName: resources.defaultOrderTemplateName,
                        createOrderTemplateButtonText: resources.createOrderTemplateButtonText,
                        cancelNewOrderTemplateCreationButtonText: resources.cancelNewOrderTemplateCreationButtonText,
                        viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
                        continueShoppingButtonText: resources.continueShoppingButtonText,
                        itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
                        itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
                        itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
                        priceFree: resources.priceFree,
                        originalPriceText: resources.originalPriceText,
                        currentPriceText: resources.currentPriceText,
                        duplicatedProductsHeader: resources.duplicatedProductsHeader,
                        duplicatedProductsDescription: resources.duplicatedProductsDescription,
                        updateQuantityButtonText: resources.updateQuantityButtonText,
                        cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText,
                        addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                        addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip
                    }}
                    onBuyValidate={(product) => onBuyHasAccessValidator(
                        context,
                        orgInformation.result,
                        product,
                        resources.toastNoAccessToProduct
                    )}
                />
            )
        };
    }

    private _getActivePrices = async (): Promise<void> => {
        const { context } = this.props;
        const products = await this.products;

        const ids = products.map(x => x.RecordId) || [];

        if (!ids || ids.length === 0) {
            this.setState({ retrievingActivePrices: false });
            return;
        }

        const prices = await getActivePricesAsync(
            { callerContext: context.actionContext },
            {
                CatalogId: context.request.apiSettings.catalogId,
                ChannelId: context.request.apiSettings.channelId
            },
            ids,
            new Date(),
            null,
            undefined,
            true
        );

        for (const product of products) {
            const price = prices.find(p => p.ProductId === product.RecordId);
            if (price) {
                product.Price = price.CustomerContextualPrice || 0;
                product.BasePrice = price.AdjustedPrice || 0;
                product.Badge = getDiscountBadge(price, product.FallbackBadge);
                product.ProductPrice = price;
                product.PrefetchedPrice = true;
            }
        }

        // Sort products with discounts first to ensure the most relevant products are shown first
        const copy = [...products];
        copy.sort((a, b) => {
            const aDiscounts = getDiscountLines(a.ProductPrice?.DiscountLines);
            const bDiscounts = getDiscountLines(b.ProductPrice?.DiscountLines);

            if (aDiscounts.length > 0 && bDiscounts.length === 0) {
                return -1;
            } else if (aDiscounts.length === 0 && bDiscounts.length > 0) {
                return 1;
            } else {
                return 0;
            }
        });

        this.products.result = copy;

        this.setState({ retrievingActivePrices: false, hasSortedProducts: true });
    };

    private _getSimpleProducts = async(): Promise<void> => {
        const { context } = this.props;
        const products = await this.products;
        const ids = products.map(x => x.RecordId) || [];

        if (!ids || ids.length === 0) {
            return;
        }

        const simpleProducts = await getByIdsAsync({ callerContext: context.actionContext }, context.request.apiSettings.channelId, ids);
        this.setState({ simpleProducts });
    };

    private _getUnitConversions = async(): Promise<void> => {
        const { actionContext, request, app } = this.props.context;

        if (!request.user.isAuthenticated || app.config.unitOfMeasureConversions !== true) {
            return;
        }

        const products = await this.products;
        const ids = products.map(x => x.RecordId);

        if (!ids || ids.length === 0) {
            return;
        }

        const conversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, ids);

        this.setState({ unitConversions: conversions });
    };

    private _getProductListsByProductIds = async (): Promise<void> => {
        const { context } = this.props;

        if (!context.request.user.isAuthenticated) {
            return;
        }

        const products = await this.products;
        const ids = products.map(x => x.RecordId);

        if (!ids || ids.length === 0) {
            return;
        }

        const result = await getProductListsByProductIdsAsync({ callerContext: context.actionContext }, ids);

        products.forEach(product => {
            product.PartOfProductLists = result.filter(x => x.ProductId === product.RecordId);
        });
    };

    private _getEstimatedAvailabilities = async (): Promise<void> => {
        const { context } = this.props;
        const products = await this.products;

        if (!products || products.length === 0) {
            return;
        }

        const ids = products.map(x => x.RecordId) || [];

        if (!ids || ids.length === 0) {
            return;
        }

        const criteria = createInventoryAvailabilitySearchCriteria(
            context.actionContext,
            ids,
            true,
            undefined,
            undefined,
            DeliveryMode.shipping
        );
        const shippingAvailabilities = await getEstimatedAvailabilityAsync({ callerContext: context.actionContext }, criteria);

        if (shippingAvailabilities.ProductWarehouseInventoryAvailabilities) {
            const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, shippingAvailabilities);

            if (ArrayExtensions.hasElements(shippingInventory)) {
                shippingInventory.forEach(inventory => inventory.deliveryType = DeliveryMode.shipping );
                this.setState({ availabilities: shippingInventory });
            }
        }
    };

    private _retrieveOrderTemplates: () => Promise<OrderTemplate[]> = async () => {
        return this.state.orderTemplates || this._onUpdateOrderTemplates();
    };

    private _onUpdateOrderTemplates: (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => Promise<OrderTemplate[]> = async (product, newOrderTemplate) => {
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(this.state.orderTemplates ? this.state.orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: this.props.context.request,
                config: this.props.config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, this.props.context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        this.setState({ orderTemplates: newOrderTemplates });
        return newOrderTemplates;
    };
}

export default DlvryAlternativeProducts;
